import { getAllArgumentNames } from './function-definitions.ts'
import { Token, TokenType } from './lexer.ts'

// AST Node types
export type ASTNode =
  | ProgramNode
  | AssignmentNode
  | PipelineNode
  | CallNode
  | ArrowFunctionNode
  | BlockNode
  | ReturnNode
  | BinaryOpNode
  | MemberAccessNode
  | IdentifierNode
  | LiteralNode
  | SequenceNode
  | NoteNode
  | ArrayLiteralNode
  | NumberWithParamsNode

export interface ProgramNode {
  type: 'Program'
  statements: ASTNode[]
}

export interface AssignmentNode {
  type: 'Assignment'
  name: string
  value: ASTNode
}

export interface PipelineNode {
  type: 'Pipeline'
  stages: ASTNode[]
}

export interface CallNode {
  type: 'Call'
  name: string
  args: ArgumentNode[]
  line?: number
  column?: number
}

export interface ArgumentNode {
  name?: string // For named args like hz:440
  value: ASTNode
}

export interface ArrowFunctionParamNode {
  name: string
  defaultValue?: number
}

export interface ArrowFunctionNode {
  type: 'ArrowFunction'
  params: string[]
  paramDefaults?: Map<string, number>
  body: ASTNode
}

export interface BlockNode {
  type: 'Block'
  statements: ASTNode[]
}

export interface ReturnNode {
  type: 'Return'
  value: ASTNode
}

export interface BinaryOpNode {
  type: 'BinaryOp'
  operator: string
  left: ASTNode
  right: ASTNode
}

export interface MemberAccessNode {
  type: 'MemberAccess'
  object: ASTNode
  property: string
  line?: number
  column?: number
}

export interface IdentifierNode {
  type: 'Identifier'
  name: string
  line?: number
  column?: number
}

export interface LiteralNode {
  type: 'Literal'
  value: number | string
  line?: number
  column?: number
  sourceLength?: number
  noteText?: string
  glide?: number
}

export interface SequenceNode {
  type: 'Sequence'
  pattern: string // The raw sequence string
  notes: NoteNode[]
  line?: number
  column?: number
}

export interface NoteNode {
  type: 'Note'
  pitches: string[] // e.g., ['c4', 'e4'] or ['_']
  duration: string // e.g., '1/8' or '1/8+1/16'
  velocity: number // 0-1
}

export interface ArrayLiteralNode {
  type: 'ArrayLiteral'
  elements: ASTNode[]
  isNoteArray?: boolean
}

export interface NumberWithParamsNode {
  type: 'NumberWithParams'
  value: number
  params: ArgumentNode[]
  line?: number
  column?: number
}

export class Parser {
  private tokens: Token[]
  private position = 0
  private knownParamNames: Set<string>
  private static readonly NOTE_PATTERN = /[a-g][#b]?\d+/gi
  private static readonly SIMPLE_NOTE_PATTERN = /^[a-g][#b]?\d+$/i
  private static readonly NOTE_SEQUENCE_PATTERN = /[a-g_].*:.*@/
  private static readonly CONCATENATED_CHECK_PATTERN = /\s|:|@/
  private static readonly DURATION_CHECK_PATTERN = /[^0-9\s+\-*/().]/
  private static readonly NUMBER_PATTERN = /^-?(\d+\.?\d*|\.\d+)$/
  private static readonly NOTE_MATCH_PATTERN = /([a-g][#b]?)(\d+)/i
  private static readonly NOTE_NAMES = ['c', 'c#', 'd', 'd#', 'e', 'f', 'f#', 'g', 'g#', 'a', 'a#', 'b']
  private static readonly COMPOUND_OPERATORS = new Map<TokenType, string>([
    [TokenType.PLUS_ASSIGN, '+'],
    [TokenType.MINUS_ASSIGN, '-'],
    [TokenType.MULTIPLY_ASSIGN, '*'],
    [TokenType.DIVIDE_ASSIGN, '/'],
    [TokenType.POWER_ASSIGN, '^'],
  ])
  private static readonly COMPOUND_TOKENS = new Set<TokenType>([
    TokenType.PLUS_ASSIGN,
    TokenType.MINUS_ASSIGN,
    TokenType.MULTIPLY_ASSIGN,
    TokenType.DIVIDE_ASSIGN,
    TokenType.POWER_ASSIGN,
  ])

  constructor(tokens: Token[]) {
    // Filter out newlines for easier parsing
    this.tokens = tokens.filter(t => t.type !== TokenType.NEWLINE)
    // Build set of known parameter names for shorthand syntax
    const paramNames = getAllArgumentNames()
    this.knownParamNames = new Set(paramNames.map(name => name.toLowerCase()))
  }

  parse(): ProgramNode {
    const statements: ASTNode[] = []

    while (!this.isAtEnd()) {
      const stmt = this.parseStatement()
      if (stmt) statements.push(stmt)
    }

    return { type: 'Program', statements }
  }

  private parseStatement(): ASTNode | null {
    if (this.check(TokenType.RETURN)) {
      return this.parseReturn()
    }

    // Check for assignment (IDENTIFIER = ... or compound assignment)
    if (this.check(TokenType.IDENTIFIER) && this.nextTokenIsAssignmentOperator()) {
      return this.parseAssignment()
    }

    // Otherwise, parse expression statement
    return this.parseExpression()
  }

  private parseAssignment(): AssignmentNode {
    const nameToken = this.advance()
    const operatorToken = this.peek()

    if (operatorToken.type !== TokenType.ASSIGN && !Parser.COMPOUND_TOKENS.has(operatorToken.type)) {
      throw new Error(
        `Expected assignment operator after identifier at line ${operatorToken.line}, column ${operatorToken.column}, got '${operatorToken.value}'`,
      )
    }

    this.advance()
    const rhs = this.parseExpression()

    const value = operatorToken.type === TokenType.ASSIGN
      ? rhs
      : {
        type: 'BinaryOp',
        operator: this.getCompoundOperatorSymbol(operatorToken.type),
        left: {
          type: 'Identifier',
          name: nameToken.value,
          line: nameToken.line,
          column: nameToken.column,
        },
        right: rhs,
      } as BinaryOpNode

    return { type: 'Assignment', name: nameToken.value, value }
  }

  private nextTokenIsAssignmentOperator(): boolean {
    const next = this.tokens[this.position + 1]
    return next ? (next.type === TokenType.ASSIGN || Parser.COMPOUND_TOKENS.has(next.type)) : false
  }

  private isCompoundAssignmentToken(type?: TokenType): boolean {
    return type ? Parser.COMPOUND_TOKENS.has(type) : false
  }

  private getCompoundOperatorSymbol(type: TokenType): string {
    const op = Parser.COMPOUND_OPERATORS.get(type)
    if (!op) throw new Error(`Unsupported compound assignment operator: ${type}`)
    return op
  }

  private parseReturn(): ReturnNode {
    this.consume(TokenType.RETURN, 'Expected \'return\'')
    const value = this.parseExpression()
    return { type: 'Return', value }
  }

  private parseExpression(): ASTNode {
    // Check for single-parameter arrow function: param -> body or param=default -> body
    if (this.check(TokenType.IDENTIFIER)) {
      if (this.checkAhead(1, TokenType.ARROW)) {
        const param = this.advance().value
        this.advance() // consume '->'
        const body = this.parseArrowFunctionBody()
        return { type: 'ArrowFunction', params: [param], body }
      }
      else if (this.checkAhead(1, TokenType.ASSIGN) && this.checkAhead(2, TokenType.NUMBER)) {
        // Check if this looks like param=value -> body
        const checkpoint = this.position
        const param = this.advance().value
        this.advance() // consume '='
        const defaultValue = parseFloat(this.advance().value)
        if (this.check(TokenType.ARROW)) {
          this.advance() // consume '->'
          const body = this.parseArrowFunctionBody()
          const paramDefaults = new Map<string, number>()
          paramDefaults.set(param, defaultValue)
          return { type: 'ArrowFunction', params: [param], paramDefaults, body }
        }
        // Not an arrow function, backtrack
        this.position = checkpoint
      }
    }

    return this.parsePipeline()
  }

  private parsePipeline(): ASTNode {
    let left = this.parseAdditive()

    if (!this.match(TokenType.PIPE)) {
      return left
    }

    const stages = [left, this.parseAdditive()]
    while (this.match(TokenType.PIPE)) {
      stages.push(this.parseAdditive())
    }

    return { type: 'Pipeline', stages }
  }

  private parseAdditive(): ASTNode {
    let left = this.parseMultiplicative()

    while (this.match(TokenType.PLUS, TokenType.MINUS)) {
      const operator = this.previous().value
      const right = this.parseMultiplicative()
      left = { type: 'BinaryOp', operator, left, right }
    }

    return left
  }

  private parseMultiplicative(): ASTNode {
    let left = this.parsePower()

    while (this.match(TokenType.MULTIPLY, TokenType.DIVIDE)) {
      const operator = this.previous().value
      const right = this.parsePower()
      left = { type: 'BinaryOp', operator, left, right }
    }

    return left
  }

  private parsePower(): ASTNode {
    let left = this.parseUnary()

    if (this.match(TokenType.POWER)) {
      const right = this.parsePower()
      return { type: 'BinaryOp', operator: '^', left, right }
    }

    return left
  }

  private parseUnary(): ASTNode {
    // Handle unary minus
    if (this.match(TokenType.MINUS)) {
      // Check if this is a negative number with parameters: -5(min:..., max:...)
      if (this.check(TokenType.NUMBER)) {
        const minusToken = this.previous()
        const numberToken = this.advance()
        const value = -parseFloat(numberToken.value)
        const sourceLength = 1 + numberToken.value.length // '-' + number length

        // Check if followed by parameters
        if (this.check(TokenType.LPAREN)) {
          const params = this.parseArguments()
          return { type: 'NumberWithParams', value, params, line: minusToken.line, column: minusToken.column }
        }

        return { type: 'Literal', value, line: minusToken.line, column: minusToken.column, sourceLength }
      }

      const right = this.parseUnary()
      // Convert to binary operation: 0 - right
      return {
        type: 'BinaryOp',
        operator: '-',
        left: { type: 'Literal', value: 0 },
        right,
      }
    }

    let node = this.parseMemberAccess()

    // Handle postfix unipolar operator ^^ (desugars to *0.5+0.5)
    if (this.match(TokenType.UNIPOLAR)) {
      // Desugar: expr^^ -> (expr * 0.5) + 0.5
      node = {
        type: 'BinaryOp',
        operator: '+',
        left: {
          type: 'BinaryOp',
          operator: '*',
          left: node,
          right: { type: 'Literal', value: 0.5 },
        },
        right: { type: 'Literal', value: 0.5 },
      }
    }

    return node
  }

  private parseMemberAccess(): ASTNode {
    let node = this.parsePrimary()

    while (this.match(TokenType.DOT)) {
      const propertyToken = this.consume(TokenType.IDENTIFIER, 'Expected property name')
      const property = propertyToken.value

      // Prevent member access on % (current value)
      if (node.type === 'Identifier' && node.name === '%') {
        throw new Error(`No member access ${property} for %`)
      }

      // Check for method call
      if (this.check(TokenType.LPAREN)) {
        const args = this.parseArguments()
        node = {
          type: 'Call',
          name: property,
          args: [{ value: node }, ...args], // First arg is the object
          line: propertyToken.line,
          column: propertyToken.column,
        }
      }
      else {
        node = {
          type: 'MemberAccess',
          object: node,
          property,
          line: propertyToken.line,
          column: propertyToken.column,
        }
      }
    }

    return node
  }

  private parsePrimary(): ASTNode {
    // Numbers (possibly with parameters)
    if (this.match(TokenType.NUMBER)) {
      const numberToken = this.previous()
      const value = parseFloat(numberToken.value)
      const sourceLength = numberToken.value.length // Store original token length

      // Check if followed by parameters: number(param:value, ...)
      if (this.check(TokenType.LPAREN)) {
        const params = this.parseArguments()
        return { type: 'NumberWithParams', value, params, line: numberToken.line, column: numberToken.column }
      }

      return { type: 'Literal', value, line: numberToken.line, column: numberToken.column, sourceLength }
    }

    // Strings
    if (this.match(TokenType.STRING)) {
      const token = this.previous()
      const value = token.value
      const isDoubleQuoted = token.quoteType === '"'

      if (this.check(TokenType.DOT)) {
        this.validateAsNoteSequence(value, isDoubleQuoted)
      }

      if (isDoubleQuoted) {
        if (this.isSimpleNoteSequence(value)) {
          return this.parseSimpleNoteSequence(value, token.line, token.column)
        }
        const stripped = value.replace(/\s+/g, '')
        if (this.isConcatenatedNoteSequence(stripped)) {
          return this.parseConcatenatedNoteSequence(value, token.line, token.column)
        }
        if (this.isNoteSequence(value)) {
          return this.parseNoteSequence(value, token.line, token.column)
        }
      }
      else {
        if (this.isSimpleNoteSequence(value)) {
          return this.parseSimpleNoteSequence(value, token.line, token.column)
        }
        if (this.isNoteSequence(value)) {
          return this.parseNoteSequence(value, token.line, token.column)
        }
      }

      return { type: 'Literal', value, line: token.line, column: token.column }
    }

    // Percent (current value)
    if (this.match(TokenType.PERCENT)) {
      // Check if followed by a number starting with . (like %.2) - this should be an error
      if (this.check(TokenType.NUMBER)) {
        const nextToken = this.peek()
        if (nextToken.value.startsWith('.')) {
          // Extract the property name (digits after the dot)
          const property = nextToken.value.substring(1)
          throw new Error(`No member access ${property} for %`)
        }
      }
      return { type: 'Identifier', name: '%' }
    }

    // Identifiers and function calls
    if (this.match(TokenType.IDENTIFIER)) {
      const token = this.previous()
      const name = token.value

      // Function call
      if (this.check(TokenType.LPAREN)) {
        const args = this.parseArguments()
        return { type: 'Call', name, args, line: token.line, column: token.column }
      }

      return { type: 'Identifier', name, line: token.line, column: token.column }
    }

    // Parenthesized expression or arrow function
    if (this.match(TokenType.LPAREN)) {
      const checkpoint = this.position
      const params: string[] = []
      const paramDefaults = new Map<string, number>()

      // Try to parse as arrow function params
      if (this.tryParseArrowFunctionParams(params, paramDefaults, checkpoint)) {
        if (this.check(TokenType.RPAREN)) {
          this.advance()
          if (this.match(TokenType.ARROW)) {
            return {
              type: 'ArrowFunction',
              params,
              paramDefaults: paramDefaults.size > 0 ? paramDefaults : undefined,
              body: this.parseArrowFunctionBody(),
            }
          }
        }
      }

      // Not an arrow function, backtrack and parse as expression
      this.position = checkpoint
      const expr = this.parseExpression()
      this.consume(TokenType.RPAREN, 'Expected \')\'')
      return expr
    }

    // Array literal
    if (this.match(TokenType.LBRACKET)) {
      return this.parseArrayLiteral()
    }

    // Block
    if (this.match(TokenType.LBRACE)) {
      return this.parseBlock()
    }

    throw new Error(
      `Unexpected token: ${this.peek().value} at line ${this.peek().line}, column ${this.peek().column}`,
    )
  }

  private parseArrayLiteral(): ArrayLiteralNode {
    const elements: ASTNode[] = []

    if (!this.check(TokenType.RBRACKET)) {
      do {
        elements.push(this.parseExpression())
      }
      while (this.match(TokenType.COMMA))
    }

    this.consume(TokenType.RBRACKET, 'Expected \']\'')
    return { type: 'ArrayLiteral', elements }
  }

  private parseArguments(): ArgumentNode[] {
    this.consume(TokenType.LPAREN, 'Expected \'(\'')

    const args: ArgumentNode[] = []

    if (!this.check(TokenType.RPAREN)) {
      do {
        if (this.check(TokenType.IDENTIFIER)) {
          const nameToken = this.peek()
          const name = nameToken.value

          if (this.checkAhead(1, TokenType.COLON)) {
            // Named argument: name:value
            this.advance()
            this.advance()
            args.push({ name, value: this.parseExpression() })
          }
          else if (
            (this.checkAhead(1, TokenType.COMMA) || this.checkAhead(1, TokenType.RPAREN))
            && this.knownParamNames.has(name.toLowerCase())
          ) {
            // Shorthand: name (expands to name:name)
            this.advance()
            args.push({ name, value: { type: 'Identifier', name } })
          }
          else {
            args.push({ value: this.parseExpression() })
          }
        }
        else {
          args.push({ value: this.parseExpression() })
        }
      }
      while (this.match(TokenType.COMMA))
    }

    this.consume(TokenType.RPAREN, 'Expected \')\'')
    return args
  }

  private tryParseArrowFunctionParams(
    params: string[],
    paramDefaults: Map<string, number>,
    checkpoint: number,
  ): boolean {
    // Handle first parameter
    if (this.check(TokenType.IDENTIFIER)) {
      if (!this.tryParseParamWithDefault(params, paramDefaults, checkpoint)) {
        return false
      }
    }
    else if (this.check(TokenType.COMMA)) {
      params.push('_')
    }
    else if (!this.check(TokenType.RPAREN)) {
      return false
    }

    // Handle remaining parameters
    while (this.match(TokenType.COMMA)) {
      if (this.check(TokenType.IDENTIFIER)) {
        if (!this.tryParseParamWithDefault(params, paramDefaults, checkpoint)) {
          return false
        }
      }
      else if (this.check(TokenType.COMMA) || this.check(TokenType.RPAREN)) {
        params.push('_')
      }
      else {
        return false
      }
    }

    return true
  }

  private tryParseParamWithDefault(
    params: string[],
    paramDefaults: Map<string, number>,
    checkpoint: number,
  ): boolean {
    const paramName = this.advance().value
    params.push(paramName)

    if (this.check(TokenType.COLON)) {
      const token = this.peek()
      throw new Error(
        `Expected '=' for default value in function parameter definition at line ${token.line}, column ${token.column}, got ':'`,
      )
    }

    if (!this.check(TokenType.ASSIGN)) {
      return true
    }

    this.advance()
    let defaultValue: number

    if (this.check(TokenType.NUMBER)) {
      defaultValue = parseFloat(this.advance().value)
    }
    else if (this.check(TokenType.MINUS) && this.checkAhead(1, TokenType.NUMBER)) {
      this.advance()
      defaultValue = -parseFloat(this.advance().value)
    }
    else {
      this.position = checkpoint
      return false
    }

    paramDefaults.set(paramName, defaultValue)
    return true
  }

  private parseArrowFunctionBody(): ASTNode {
    return this.check(TokenType.LBRACE) ? this.parseBlock() : this.parseExpression()
  }

  private parseBlock(): BlockNode {
    this.consume(TokenType.LBRACE, 'Expected \'{\'')

    const statements: ASTNode[] = []
    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      const stmt = this.parseStatement()
      if (stmt) statements.push(stmt)
    }

    this.consume(TokenType.RBRACE, 'Expected \'}\'')
    return { type: 'Block', statements }
  }

  private parseNoteSequence(pattern: string, line?: number, column?: number): SequenceNode {
    const trimmed = pattern.trim()
    const tokens = trimmed.split(/\s+/).filter(noteStr => noteStr.length > 0)

    // Check for invalid tokens that don't look like notes (must contain : and @)
    for (const token of tokens) {
      if (!token.includes(':') || !token.includes('@')) {
        throw new Error(
          `Invalid token "${token}" in sequence. All tokens must be valid notes in the format: note:duration@velocity,hold (e.g., "c4:1/4@1" or "_:1/8@0,.5" where hold is optional).`,
        )
      }
    }

    const notes = tokens.map(noteStr => this.parseNote(noteStr))
    return { type: 'Sequence', pattern, notes, line, column }
  }

  private parseNote(noteStr: string): NoteNode {
    const colonIndex = noteStr.indexOf(':')
    if (colonIndex === -1) {
      throw new Error(
        `Invalid note format: ${noteStr}. Notes must be in the format: pitch:duration@velocity,hold (e.g., "c4:1/4@1" or "_:1/8@0,.5" where hold is optional).`,
      )
    }

    const pitchPart = noteStr.slice(0, colonIndex).trim()
    const restPart = noteStr.slice(colonIndex + 1)

    // Validate pitch part - must be either '_' or valid note(s)
    let pitches: string[]
    if (pitchPart === '_') {
      pitches = ['_']
    }
    else {
      if (pitchPart.includes('_')) {
        throw new Error(
          `Invalid pitch format "${pitchPart}" in note: ${noteStr}. Underscore (_) can only be used alone for rests, not combined with other characters.`,
        )
      }

      Parser.NOTE_PATTERN.lastIndex = 0
      const matches = pitchPart.match(Parser.NOTE_PATTERN)
      if (!matches || matches.length === 0) {
        throw new Error(
          `Invalid pitch format "${pitchPart}" in note: ${noteStr}. Pitch must be a valid note (e.g., "c4", "d#3", "eb2") or "_" for a rest.`,
        )
      }

      Parser.NOTE_PATTERN.lastIndex = 0
      const withoutNotes = pitchPart.replace(Parser.NOTE_PATTERN, '')
      if (withoutNotes.trim().length > 0) {
        throw new Error(
          `Invalid pitch format "${pitchPart}" in note: ${noteStr}. Contains invalid characters. Pitch must be valid notes (e.g., "c4", "d#3", "eb2") or "_" for a rest.`,
        )
      }

      pitches = matches
    }

    // Parse duration and velocity
    const atIndex = restPart.indexOf('@')
    if (atIndex === -1) {
      throw new Error(`Invalid note format (missing @): ${noteStr}`)
    }

    const duration = restPart.slice(0, atIndex).trim()
    const velocityHoldStr = restPart.slice(atIndex + 1).trim()

    // Validate duration format
    if (!duration || Parser.DURATION_CHECK_PATTERN.test(duration)) {
      throw new Error(
        `Invalid duration format "${duration}" in note: ${noteStr}. Duration must be a number or fraction (e.g., "1/4", "7/8", "1/8+1/16").`,
      )
    }

    // Try to parse duration to validate it's a valid expression
    try {
      Function(`"use strict"; return (${duration})`)()
    }
    catch {
      throw new Error(
        `Invalid duration expression "${duration}" in note: ${noteStr}. Duration must be a valid mathematical expression (e.g., "1/4", "7/8", "1/8+1/16").`,
      )
    }

    // Split velocity and hold
    const commaIndex = velocityHoldStr.indexOf(',')
    const velocityStr = commaIndex >= 0 ? velocityHoldStr.slice(0, commaIndex).trim() : velocityHoldStr.trim()
    const holdStr = commaIndex >= 0 ? velocityHoldStr.slice(commaIndex + 1).trim() : null

    // Validate hold value if present
    if (holdStr !== null && holdStr !== '') {
      if (!Parser.NUMBER_PATTERN.test(holdStr)) {
        throw new Error(`Invalid hold value "${holdStr}" in note: ${noteStr}. Hold must be a number.`)
      }
    }
    else if (holdStr === '') {
      throw new Error(
        `Invalid hold value in note: ${noteStr}. Hold value cannot be empty. If you don't need a hold value, omit the comma.`,
      )
    }

    // Parse velocity
    let velocity: number
    if (velocityStr === '_') {
      velocity = -1
    }
    else {
      if (!Parser.NUMBER_PATTERN.test(velocityStr)) {
        throw new Error(
          `Invalid velocity value "${velocityStr}" in note: ${noteStr}. Velocity must be a number (0-1) or "_".`,
        )
      }
      velocity = parseFloat(velocityStr)
      if (isNaN(velocity)) {
        throw new Error(
          `Invalid velocity value "${velocityStr}" in note: ${noteStr}. Velocity must be a number (0-1) or "_".`,
        )
      }
    }

    return { type: 'Note', pitches, duration, velocity }
  }

  private isNoteSequence(str: string): boolean {
    return Parser.NOTE_SEQUENCE_PATTERN.test(str)
  }

  private isSimpleNoteSequence(str: string): boolean {
    const trimmed = str.trim()
    if (!trimmed) return false

    const tokens = trimmed.split(/\s+/)
    for (const token of tokens) {
      if (token !== '_' && !Parser.SIMPLE_NOTE_PATTERN.test(token)) {
        return false
      }
    }
    return true
  }

  private isConcatenatedNoteSequence(str: string): boolean {
    const trimmed = str.trim()
    if (!trimmed || Parser.CONCATENATED_CHECK_PATTERN.test(trimmed)) return false

    Parser.NOTE_PATTERN.lastIndex = 0
    let match
    let totalLength = 0

    while ((match = Parser.NOTE_PATTERN.exec(trimmed)) !== null) {
      totalLength += match[0].length
    }

    return totalLength > 0 && totalLength === trimmed.length
  }

  private validateAsNoteSequence(str: string, isDoubleQuoted: boolean): void {
    const trimmed = str.trim()
    if (!trimmed) return

    if (isDoubleQuoted && !Parser.CONCATENATED_CHECK_PATTERN.test(trimmed)) {
      // Concatenated format
      Parser.NOTE_PATTERN.lastIndex = 0
      let match
      let totalLength = 0

      while ((match = Parser.NOTE_PATTERN.exec(trimmed)) !== null) {
        totalLength += match[0].length
      }

      if (totalLength > 0 && totalLength !== trimmed.length) {
        throw new Error(
          `Invalid note syntax in "${str}". "${
            trimmed.slice(totalLength)
          }" is not a valid note. Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
        )
      }

      if (totalLength === 0) {
        throw new Error(
          `Invalid note syntax in "${str}". No valid notes found. Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
        )
      }
    }
    else {
      // Space-separated format
      const tokens = trimmed.split(/\s+/)
      for (const token of tokens) {
        if (token !== '_' && !Parser.SIMPLE_NOTE_PATTERN.test(token)) {
          throw new Error(
            `Invalid note syntax in "${str}". Note "${token}" is not valid. Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
          )
        }
      }
    }
  }

  private parseSimpleNoteSequence(pattern: string, stringLine?: number, stringColumn?: number): ArrayLiteralNode {
    const elements: ASTNode[] = []
    const currentLine = stringLine ?? 1
    const baseColumn = (stringColumn ?? 0) + 1

    // Split by whitespace to get individual tokens including glide notation
    const tokens = pattern.trim().split(/\s+/)
    let charOffset = 0

    for (const token of tokens) {
      // Skip whitespace between tokens
      while (charOffset < pattern.length && /\s/.test(pattern[charOffset])) {
        charOffset++
      }

      // Parse token: note\glide or just note
      const match = token.match(/^([a-g][#b]?\d+|_)(\\([\d.]+))?$/i)
      if (match) {
        const noteText = match[1]
        const glide = match[3] ? parseFloat(match[3]) : 0

        if (noteText === '_') {
          elements.push({
            type: 'Literal',
            value: 0,
            line: currentLine,
            column: baseColumn + charOffset,
            noteText: '_',
            glide,
          })
        }
        else {
          const freq = this.noteToFrequency(noteText)
          elements.push({
            type: 'Literal',
            value: freq,
            line: currentLine,
            column: baseColumn + charOffset,
            noteText,
            glide,
          })
        }
      }

      charOffset += token.length
    }

    return { type: 'ArrayLiteral', elements, isNoteArray: true }
  }

  private parseConcatenatedNoteSequence(pattern: string, stringLine?: number, stringColumn?: number): ArrayLiteralNode {
    const trimmed = pattern.trim()
    const elements: ASTNode[] = []
    const currentLine = stringLine ?? 1
    const baseColumn = (stringColumn ?? 0) + 1
    const trimStart = pattern.indexOf(trimmed)

    Parser.NOTE_PATTERN.lastIndex = 0
    let match
    let totalLength = 0

    while ((match = Parser.NOTE_PATTERN.exec(trimmed)) !== null) {
      const note = match[0]
      totalLength += note.length
      const freq = this.noteToFrequency(note)
      elements.push({
        type: 'Literal',
        value: freq,
        line: currentLine,
        column: baseColumn + trimStart + match.index,
        noteText: note,
      })
    }

    if (totalLength !== trimmed.length) {
      throw new Error(
        `Invalid note syntax in "${pattern}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
      )
    }

    return { type: 'ArrayLiteral', elements, isNoteArray: true }
  }

  private noteToFrequency(note: string): number {
    if (note === '_') return 0

    Parser.NOTE_MATCH_PATTERN.lastIndex = 0
    const match = Parser.NOTE_MATCH_PATTERN.exec(note)
    if (!match || match[0] !== note) {
      if (/[a-z]/i.test(note) && /\d/.test(note)) {
        throw new Error(
          `Invalid note syntax "${note}". Notes must be in the format: letter (a-g) + optional accidental (#/b) + octave number (e.g., "c3", "d#4", "eb2").`,
        )
      }
      return 0
    }

    let noteName = match[1].toLowerCase()
    let octave = parseInt(match[2], 10)

    // Handle sharp notes
    if (noteName.endsWith('#')) {
      const baseNote = noteName[0]
      const baseIndex = Parser.NOTE_NAMES.indexOf(baseNote)
      if (baseIndex !== -1 && baseIndex < Parser.NOTE_NAMES.length - 1) {
        noteName = Parser.NOTE_NAMES[baseIndex + 1]
      }
      else if (baseNote === 'b') {
        noteName = 'c'
        octave++
      }
      else if (baseNote === 'e') {
        noteName = 'f'
      }
    }

    // Handle flat notes
    if (noteName.endsWith('b')) {
      const baseNote = noteName[0]
      const baseIndex = Parser.NOTE_NAMES.indexOf(baseNote)
      if (baseIndex > 0) {
        noteName = Parser.NOTE_NAMES[baseIndex - 1]
      }
      else if (baseNote === 'c') {
        noteName = 'b'
        octave--
      }
      else if (baseNote === 'f') {
        noteName = 'e'
      }
    }

    const noteIndex = Parser.NOTE_NAMES.indexOf(noteName)
    if (noteIndex === -1) return 0

    const midiNote = (octave + 1) * 12 + noteIndex
    return 440 * Math.pow(2, (midiNote - 69) / 12)
  }

  // Helper methods
  private match(...types: TokenType[]): boolean {
    for (const type of types) {
      if (this.check(type)) {
        this.advance()
        return true
      }
    }
    return false
  }

  private check(type: TokenType): boolean {
    if (this.isAtEnd()) return false
    return this.peek().type === type
  }

  private checkAhead(offset: number, type: TokenType): boolean {
    const pos = this.position + offset
    return pos < this.tokens.length && this.tokens[pos].type === type
  }

  private advance(): Token {
    if (!this.isAtEnd()) this.position++
    return this.previous()
  }

  private isAtEnd(): boolean {
    return this.peek().type === TokenType.EOF
  }

  private peek(): Token {
    return this.tokens[this.position]
  }

  private previous(): Token {
    return this.tokens[this.position - 1]
  }

  private consume(type: TokenType, message: string): Token {
    if (this.check(type)) return this.advance()
    throw new Error(
      `${message} at line ${this.peek().line}, column ${this.peek().column}, got '${this.peek().value}'`,
    )
  }
}
